import React, { useState, useEffect } from 'react';
import {
  Badge,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Box,
  Divider,
  Button,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import {
  Notifications,
  Schedule,
  ConfirmationNumber,
  Build,
  Star,
  Announcement,
  Info,
  CheckCircle,
  Archive
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl, API_ENDPOINTS } from '../config/api';
import webSocketService from '../services/WebSocketService';

interface Notification {
  id: string;
  type: 'schedule_change' | 'ticket_request' | 'infrastructure_request' | 'points_awarded' | 'system_announcement' | 'general';
  title: string;
  message: string;
  isRead: boolean;
  priority: 'low' | 'medium' | 'high';
  createdAt: string;
  relatedEntityType?: string;
  relatedEntityId?: string;
}

const NotificationBell: React.FC = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [loading, setLoading] = useState(false);

  const open = Boolean(anchorEl);

  // Fetch notifications
  const fetchNotifications = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      const response = await fetch(`${getApiUrl(API_ENDPOINTS.NOTIFICATIONS)}?limit=20`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setNotifications(data.data.notifications || []);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch unread count
  const fetchUnreadCount = async () => {
    if (!user) return;
    
    try {
      const response = await fetch(`${getApiUrl(API_ENDPOINTS.NOTIFICATIONS)}/unread-count`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUnreadCount(data.data.count || 0);
      }
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  // Mark notification as read
  const markAsRead = async (notificationId: string) => {
    try {
      const response = await fetch(`${getApiUrl(API_ENDPOINTS.NOTIFICATIONS)}/${notificationId}/read`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        // Update local state
        setNotifications(prev => 
          prev.map(n => 
            n.id === notificationId ? { ...n, isRead: true } : n
          )
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    const unreadIds = notifications
      .filter(n => !n.isRead)
      .map(n => n.id);

    if (unreadIds.length === 0) return;

    try {
      const response = await fetch(`${getApiUrl(API_ENDPOINTS.NOTIFICATIONS)}/mark-multiple-read`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ notificationIds: unreadIds })
      });

      if (response.ok) {
        setNotifications(prev => 
          prev.map(n => ({ ...n, isRead: true }))
        );
        setUnreadCount(0);
      }
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  // Clear all notifications
  const clearAllNotifications = async () => {
    if (notifications.length === 0) return;

    try {
      const response = await fetch(`${getApiUrl(API_ENDPOINTS.NOTIFICATIONS)}/clear-all`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        setNotifications([]);
        setUnreadCount(0);
      }
    } catch (error) {
      console.error('Error clearing all notifications:', error);
    }
  };

  // Get notification icon based on type
  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'schedule_change':
        return <Schedule />;
      case 'ticket_request':
        return <ConfirmationNumber />;
      case 'infrastructure_request':
        return <Build />;
      case 'points_awarded':
        return <Star />;
      case 'system_announcement':
        return <Announcement />;
      default:
        return <Info />;
    }
  };



  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    // Format as: "YYYY-MM-DD HH:mm" or use locale-specific format
    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  useEffect(() => {
    if (user) {
      fetchNotifications();
      fetchUnreadCount();
      
      // Listen for real-time notification updates via WebSocket
      const handleNewNotification = (notificationData: Notification) => {
        // Add new notification to the top of the list
        setNotifications(prev => [notificationData, ...prev]);
        // Update unread count
        setUnreadCount(prev => prev + 1);
        // Refresh notifications to ensure consistency
        fetchNotifications();
        fetchUnreadCount();
      };

      webSocketService.on('notification-created', handleNewNotification);
      
      // Refresh every 30 seconds as fallback
      const interval = setInterval(() => {
        fetchUnreadCount();
      }, 30000);

      return () => {
        webSocketService.off('notification-created', handleNewNotification);
        clearInterval(interval);
      };
    }
  }, [user]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.isRead) {
      markAsRead(notification.id);
    }
    
    // TODO: Navigate to related entity or show details
    handleClose();
  };

  if (!user) return null;

  return (
    <>
      <IconButton
        color="inherit"
        onClick={handleClick}
        sx={{ ml: 1 }}
      >
        <Badge badgeContent={unreadCount} color="error">
          <Notifications />
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: 400,
            maxHeight: 700
          }
        }}
      >
        <Box sx={{ p: 2, borderBottom: '1px solid #eee' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1, gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="h6" sx={{ flex: '1 1 auto', minWidth: 0 }}>{t('notifications.title')}</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              {notifications.length > 0 && (
                <Button 
                  size="small" 
                  variant="outlined" 
                  color="error"
                  onClick={clearAllNotifications}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  {t('notifications.clearAll')}
                </Button>
              )}
              {unreadCount > 0 && (
                <Button 
                  size="small" 
                  onClick={markAllAsRead}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  {t('notifications.markAllAsRead')}
                </Button>
              )}
            </Box>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {t('notifications.unreadCount', { count: unreadCount })}
          </Typography>
        </Box>

        <Box sx={{ maxHeight: 600, overflow: 'auto' }}>
          {loading ? (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography>{t('notifications.loading')}</Typography>
            </Box>
          ) : notifications.length === 0 ? (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography color="text.secondary">{t('notifications.noNotifications')}</Typography>
            </Box>
          ) : (
            notifications.map((notification, index) => (
              <React.Fragment key={notification.id}>
                <MenuItem
                  onClick={() => handleNotificationClick(notification)}
                  sx={{
                    backgroundColor: notification.isRead ? 'transparent' : '#f5f5f5',
                    '&:hover': {
                      backgroundColor: notification.isRead ? '#f5f5f5' : '#eeeeee'
                    },
                    whiteSpace: 'normal',
                    alignItems: 'flex-start',
                    py: 1.5
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, pt: 0.5 }}>
                    {getNotificationIcon(notification.type)}
                  </ListItemIcon>
                  <ListItemText
                    disableTypography
                    primary={
                      <Box>
                        <Typography
                          component="p"
                          variant="body2"
                          sx={{
                            fontWeight: notification.isRead ? 'normal' : 'bold',
                            mb: 0.5
                          }}
                        >
                          {notification.title}
                        </Typography>
                        <Typography 
                          component="p" 
                          variant="body2" 
                          color="text.secondary"
                          sx={{ 
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                            overflowWrap: 'break-word',
                            mb: 0.5
                          }}
                        >
                          {notification.message}
                        </Typography>
                        <Typography 
                          component="p" 
                          variant="caption" 
                          color="text.secondary"
                          sx={{ display: 'block', mt: 0.5 }}
                        >
                          {formatDate(notification.createdAt)}
                        </Typography>
                      </Box>
                    }
                  />
                  {!notification.isRead && (
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: 'primary.main',
                        ml: 1
                      }}
                    />
                  )}
                </MenuItem>
                {index < notifications.length - 1 && <Divider />}
              </React.Fragment>
            ))
          )}
        </Box>

        {notifications.length > 0 && (
          <Box sx={{ p: 2, borderTop: '1px solid #eee', textAlign: 'center' }}>
            <Button size="small" onClick={handleClose}>
              {t('notifications.viewAll')}
            </Button>
          </Box>
        )}
      </Menu>
    </>
  );
};

export default NotificationBell;
